import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import SubscriptionTokenResponse from "interfaces/response/SubscriptionTokenResponse";
import BaseState from "interfaces/state/BaseState";
import {
  getTokensBySubscriptionIdAsync,
  createNewTokenAsync,
  updateTokenAsync,
  regenerateTokenAsync,
  getTokenTagsByProductIdAsync,
} from "apis/TokenApi";
import SubscriptionTokenRequest from "interfaces/request/SubscriptionTokenRequest";
import { toastSuccess } from "components/common/ToastNotification";

export interface TokenState extends BaseState {
  tokens: SubscriptionTokenResponse[];
  tokensLoaded: boolean;
  isCreateTokenNotificationDisplayed: boolean;
  selectedToken: SubscriptionTokenResponse | null;
  tokenTags: {
    [productId: number]: string[];
  };
}

const initialState: TokenState = {
  loading: false,
  tokensLoaded: false,
  isCreateTokenNotificationDisplayed: false,
  tokens: [],
  selectedToken: null,
  tokenTags: {},
};

export const getTokensBySubscriptionId = createAsyncThunk(
  "token/getTokens",
  async (subscriptionId: number) => {
    return await getTokensBySubscriptionIdAsync(subscriptionId);
  }
);

export const createNewToken = createAsyncThunk(
  "token/createNewToken",
  async (tokenDetail: SubscriptionTokenRequest, thunkAPI) => {
    return await createNewTokenAsync(tokenDetail);
  }
);

export const updateToken = createAsyncThunk(
  "token/updateToken",
  async (tokenDetail: SubscriptionTokenRequest, thunkAPI) => {
    return await updateTokenAsync(tokenDetail);
  }
);

export const regenerateToken = createAsyncThunk(
  "token/regenerateToken",
  async (tokenId: number, thunkAPI) => {
    return await regenerateTokenAsync(tokenId);
  }
);

export const getTokenTags = createAsyncThunk(
  "token/getTokenTags",
  async (productId: number, thunkAPI) => {
    const tags = await getTokenTagsByProductIdAsync(productId);
    return { productId, tags };
  }
);

const getTokenWithoutKeyFromTokenResponse = (token: SubscriptionTokenResponse) => {
  // We will not store key.
  const { key, ...rest } = token;
  return rest as SubscriptionTokenResponse;
};

const updateTokenDetail = (
  tokenIdToBeUpdated: number,
  tokenDetail: SubscriptionTokenResponse,
  state: TokenState
) => {
  const existingTokenIndex = state.tokens.findIndex(
    (token: SubscriptionTokenResponse) => token.id === tokenIdToBeUpdated
  );
  if (existingTokenIndex > -1) {
    state.tokens[existingTokenIndex] = tokenDetail;
    state.selectedToken = tokenDetail;
  }
};

export const tokensSlice = createSlice({
  name: "token",
  initialState,
  reducers: {
    setTokenCreateNotificationDisplayed: (state) => {
      state.isCreateTokenNotificationDisplayed = true;
    },
    resetKeyOfSelectedToken: (state) => {
      if (state.selectedToken) {
        const token = getTokenWithoutKeyFromTokenResponse(state.selectedToken);
        updateTokenDetail(state.selectedToken.id, token, state);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTokensBySubscriptionId.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getTokensBySubscriptionId.fulfilled, (state, action) => {
      state.loading = false;
      state.tokens = action.payload;
      state.tokensLoaded = true;
    });
    builder.addCase(getTokensBySubscriptionId.rejected, (state, action) => {
      state.loading = false;
      state.tokens = [];
    });
    builder.addCase(createNewToken.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(createNewToken.fulfilled, (state, action) => {
      state.loading = false;
      state.tokens.unshift(action.payload);
      state.selectedToken = action.payload;
    });
    builder.addCase(createNewToken.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(updateToken.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(updateToken.fulfilled, (state, action) => {
      state.loading = false;
      updateTokenDetail(action.payload.id, action.payload, state);
      toastSuccess("Token updated.");
    });
    builder.addCase(updateToken.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(regenerateToken.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(regenerateToken.fulfilled, (state, action) => {
      state.loading = false;
      updateTokenDetail(action.payload.id, action.payload, state);
      toastSuccess("Token regenerated.");
    });
    builder.addCase(regenerateToken.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getTokenTags.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getTokenTags.fulfilled, (state, action) => {
      state.loading = false;
      state.tokenTags[action.payload.productId] = action.payload.tags;
    });
    builder.addCase(getTokenTags.rejected, (state, action) => {
      state.loading = false;
    });
  },
});

export const { setTokenCreateNotificationDisplayed, resetKeyOfSelectedToken } =
  tokensSlice.actions;

export default tokensSlice.reducer;
